import { Dayjs } from 'dayjs'
import dayjs, { DayjsInput } from 'utils/dayjs'
import { DATE_FORMAT, PST_TZ } from '~constants'
import { ArtistProps, CalendarDayEventProps } from './types'
import { EventsByDate, FormattedEvent } from './types/Event'
import logger from 'utils/logger-aws'
import { DATE_FORMATTER } from './constants'
import cx from 'classnames'

export const getEventTimes = (events: FormattedEvent[], artistId: string) => {
  return events
    .filter(event => event.artistId == artistId)
    .map(item => dayjs(item.dateTime).format(DATE_FORMATTER.TIME))
}

export const generateCalendarStartAndEndDate = (startDate: DayjsInput) => {
  // Date should be valid format
  if (!startDate) {
    logger.error(' [generateCalendarStartAndEndDate] - Date is required to generate start and end date')
  }
  // Assuming date is a valid DayjsInput, we can generate start and end dates
  const currentStartDate = startDate
    ? dayjs(startDate as string)
        .startOf('month')
        .startOf('day')
    : dayjs.tz(PST_TZ).startOf('day')

  const calendarStartDate = currentStartDate.format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
  // Check if current date is in the last week of the month
  const isLastWeekOfMonth = currentStartDate.isSame(currentStartDate.clone().endOf('month'), 'week')
  if (isLastWeekOfMonth) {
    const endOfCurrentMonth = currentStartDate.clone().endOf('month')
    let nextMonthEndDate = endOfCurrentMonth
      .clone()
      .add(1, 'day')
      .endOf('week')
      .format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)

    const endOfMonthDay = endOfCurrentMonth.day()
    if (endOfMonthDay >= 0 && endOfMonthDay <= 2) {
      // Sunday, Monday, Tuesday
      nextMonthEndDate = endOfCurrentMonth
        .clone()
        .add(6 - endOfMonthDay, 'days')
        .format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    } else {
      // Wednesday, Thursday, Friday, Saturday
      nextMonthEndDate = endOfCurrentMonth
        .clone()
        .add(7 + (6 - endOfMonthDay), 'days')
        .format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    }
    return {
      startDate: calendarStartDate,
      endDate: nextMonthEndDate
    }
  } else {
    let endOfMonth = currentStartDate.clone().endOf('month')
    if (['Sunday', 'Monday', 'Tuesday'].includes(endOfMonth.format(DATE_FORMAT.DAY_NAME))) {
      endOfMonth = endOfMonth.endOf('week')
    } else {
      endOfMonth = endOfMonth.endOf('week').add(1, 'week').endOf('week')
    }
    const calendarEndDate = endOfMonth.format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    return {
      startDate: calendarStartDate,
      endDate: calendarEndDate
    }
  }
}

export const collectArtistIds = (artists: ArtistProps[]) => {
  if (!artists?.length) return ''
  return artists.map(artist => artist?.artistId).join(',')
}

export const isNextButtonDisabled = (selectedMonth: string, activeMonthList: string[]) => {
  if (!selectedMonth || !activeMonthList?.length) return true
  const currentMonthIndex = activeMonthList.indexOf(selectedMonth)
  return currentMonthIndex === activeMonthList.length - 1
}

export const isPrevButtonDisabled = (selectedMonth: string, activeMonthList: string[]) => {
  if (!selectedMonth || !activeMonthList?.length) return true
  const currentMonthIndex = activeMonthList.indexOf(selectedMonth)
  return currentMonthIndex === 0
}

//Generate visible calendar days for selected month
export const getCalendarDays = (
  cmStartDate: Dayjs,
  startDate: Dayjs,
  endDate: Dayjs,
  eventsByDate: EventsByDate
): CalendarDayEventProps[][] => {
  const today = dayjs()
  const calendarDays: CalendarDayEventProps[][] = []
  let week: CalendarDayEventProps[] = []

  for (let date = startDate.clone(); date.isSameOrBefore(endDate, 'day'); date = date.add(1, 'day')) {
    const dateKey = date.format(DATE_FORMAT.SHORT_DATE_FORMAT)
    week.push({
      date,
      events: eventsByDate[dateKey] ?? [],
      isFromBeforeCurrentMonth: date.isBefore(cmStartDate, 'day'),
      isCurrentDate: date.isSame(today, 'day'),
      isPastDate: date.isBefore(today, 'day')
    })

    if (week.length === 7) {
      calendarDays.push(week)
      week = []
    }
  }

  if (week.length > 0) {
    calendarDays.push(week)
  }

  return calendarDays
}

export const getCalendarDayClasses = (eventInfo: CalendarDayEventProps) => {
  if (!eventInfo?.events) {
    return cx({ calendar__day: true })
  }
  const today = dayjs()
  const eventDate = eventInfo.date
  const isPastDate = eventInfo.isPastDate
  const dayHasEvents = eventInfo.events.length > 0
  const isToday = eventDate.isSame(new Date(), 'day')
  const isBeforeToday = eventDate.isBefore(today, 'day')
  const classes = {
    calendar__day: true,
    'calendar__day--is-today': isToday,
    'calendar__day--has-passed': isBeforeToday || (isPastDate && !dayHasEvents && !isToday),
    'calendar__day--is-from-different-month': eventInfo?.isFromBeforeCurrentMonth
  }

  return cx(classes)
}
